import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getEventById, getEventRegistrations, adminCancelEventRegistration } from '../../services/api';

function AdminEventRegistrations() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [cancelTarget, setCancelTarget] = useState(null); // registration being cancelled, or null
  const [cancelNote, setCancelNote] = useState('');
  const [withholdFee, setWithholdFee] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState('');

  const PLATFORM_FEE_RATE = 0.03;

  // Mirrors the backend's admin_cancel amount logic: what was actually
  // charged if this was a real card payment, otherwise what the credit-
  // covered original price was (minus any coupon discount already applied).
  const baseRefundAmount = (registration) => {
    if (!registration) return 0;
    const pricePaid = Number(registration.price_paid || 0);
    if (pricePaid > 0) return pricePaid;
    const original = Number(registration.original_price || 0);
    const discount = Number(registration.discount_amount || 0);
    return Math.max(0, original - discount);
  };

  const displayedRefundAmount = (registration, feeWithheld) => {
    const base = baseRefundAmount(registration);
    return feeWithheld ? base * (1 - PLATFORM_FEE_RATE) : base;
  };

  const load = async () => {
    try {
      setLoading(true);
      const ev = await getEventById(id);
      setEvent(ev);
      const regs = await getEventRegistrations(id);
      setRegistrations(regs.results || regs);
    } catch (e) {
      setError(e.message || 'Failed to load registrations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const openCancelDialog = (registration) => {
    setCancelTarget(registration);
    setCancelNote('');
    setWithholdFee(true);
    setCancelError('');
  };

  const closeCancelDialog = () => {
    if (cancelling) return;
    setCancelTarget(null);
    setCancelNote('');
    setCancelError('');
  };

  const handleSubmitCancel = async () => {
    if (!cancelTarget) return;

    const note = cancelNote.trim();
    if (!note) {
      setCancelError('A note is required to cancel this registration.');
      return;
    }

    const base = baseRefundAmount(cancelTarget);
    let priceText;
    if (base === 0) {
      priceText = 'No payment was made for this registration, so no refund is needed.';
    } else {
      const amount = displayedRefundAmount(cancelTarget, withholdFee).toFixed(2);
      const feeNote = withholdFee ? ' (a 3% payment processing fee has been deducted)' : '';
      priceText = `$${amount} will be refunded automatically${feeNote} - via Stripe if it was a card payment, or as account credit if it was covered by credits.`;
    }

    const confirmed = window.confirm(
      `Cancel ${cancelTarget.attendee_name}'s registration for "${event?.name}"?\n\n${priceText}\n\nThis cannot be undone. Continue?`
    );
    if (!confirmed) return;

    setCancelling(true);
    setCancelError('');
    try {
      const result = await adminCancelEventRegistration(cancelTarget.id, note, withholdFee);
      const refund = result.refund || {};
      const refundMessages = {
        stripe_refunded: `$${(refund.amount ?? 0).toFixed(2)} refunded via Stripe.`,
        credited: `$${(refund.amount ?? 0).toFixed(2)} issued as account credit.`,
        already_refunded: 'This payment was already refunded previously; no duplicate refund was issued.',
        not_applicable: 'No refund was needed (free registration).',
      };
      setSuccess(
        `Cancelled ${cancelTarget.attendee_name}'s registration. ${refundMessages[refund.status] || ''}`
      );
      setCancelTarget(null);
      setCancelNote('');
      await load();
    } catch (e) {
      setCancelError(e.message || 'Failed to cancel registration');
    } finally {
      setCancelling(false);
    }
  };

  const refundStatusLabel = {
    not_applicable: 'No refund (free)',
    stripe_refunded: 'Refunded via Stripe',
    credited: 'Refunded as credit',
    already_refunded: 'Already refunded',
  };

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">Registrations</h1>
        <div className="text-sm text-gray-600">Event: {event?.name}</div>
      </div>
      <div className="mb-4">
        <Link className="text-blue-600" to={`/admin/events/${id}`}>Back to event</Link>
      </div>
      {error && <div className="text-red-600 mb-3">{error}</div>}
      {success && <div className="text-green-700 bg-green-50 border border-green-200 rounded p-2 mb-3">{success}</div>}
      {loading ? (
        <div>Loading...</div>
      ) : (
        <div className="bg-white rounded shadow overflow-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="px-4 py-2">User</th>
                <th className="px-4 py-2">Email</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Price Paid</th>
                <th className="px-4 py-2">Registered At</th>
                <th className="px-4 py-2">Cancellation Info</th>
                <th className="px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {registrations.map(r => (
                <tr key={r.id} className="border-t align-top">
                  <td className="px-4 py-2">{r.user_email?.split('@')[0] || r.user}</td>
                  <td className="px-4 py-2">{r.user_email || '-'}</td>
                  <td className="px-4 py-2">{r.status_display || r.status}</td>
                  <td className="px-4 py-2">{r.price_paid ? `$${r.price_paid}` : 'Free'}</td>
                  <td className="px-4 py-2">{new Date(r.registration_date).toLocaleString()}</td>
                  <td className="px-4 py-2 text-xs text-gray-600">
                    {r.status === 'cancelled' ? (
                      <div className="space-y-0.5">
                        <div>Cancelled: {new Date(r.updated_at).toLocaleString()}</div>
                        <div>
                          By: {r.cancelled_by_email ? `${r.cancelled_by_email} (admin)` : `${r.user_email || 'member'} (self)`}
                        </div>
                        {r.refund_status && (
                          <div>{refundStatusLabel[r.refund_status] || r.refund_status}</div>
                        )}
                        {r.cancellation_note && (
                          <div className="italic text-gray-500">&ldquo;{r.cancellation_note}&rdquo;</div>
                        )}
                      </div>
                    ) : '-'}
                  </td>
                  <td className="px-4 py-2">
                    {r.status === 'registered' && (
                      <button
                        onClick={() => openCancelDialog(r)}
                        className="text-red-600 hover:text-red-800 underline text-sm"
                      >
                        Cancel
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {registrations.length === 0 && (
                <tr><td className="px-4 py-8 text-center text-gray-500" colSpan="7">No registrations yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {cancelTarget && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="bg-white rounded-md shadow-lg w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Cancel Registration
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Cancelling {cancelTarget.attendee_name}&rsquo;s registration for &ldquo;{event?.name}&rdquo;.
            </p>

            {baseRefundAmount(cancelTarget) > 0 && (
              <div className="bg-gray-50 border border-gray-200 rounded-md p-3 mb-4">
                <div className="flex justify-between text-sm text-gray-700 mb-2">
                  <span>Refund amount</span>
                  <span className="font-semibold">
                    ${displayedRefundAmount(cancelTarget, withholdFee).toFixed(2)}
                  </span>
                </div>
                <label className="flex items-start text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={withholdFee}
                    onChange={(e) => setWithholdFee(e.target.checked)}
                    disabled={cancelling}
                    className="mt-0.5 mr-2 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                  />
                  <span>Deduct 3% payment processing fee</span>
                </label>
              </div>
            )}

            <label className="block text-sm font-medium text-gray-700 mb-1">
              Note <span className="text-red-600">*</span>
            </label>
            <textarea
              value={cancelNote}
              onChange={(e) => setCancelNote(e.target.value)}
              rows={3}
              placeholder="Reason for cancelling this registration..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 mb-2"
              disabled={cancelling}
            />
            {cancelError && (
              <p className="text-sm text-red-600 mb-2">{cancelError}</p>
            )}
            <div className="flex justify-end gap-3 mt-4">
              <button
                type="button"
                onClick={closeCancelDialog}
                disabled={cancelling}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Close
              </button>
              <button
                type="button"
                onClick={handleSubmitCancel}
                disabled={cancelling || !cancelNote.trim()}
                className="px-4 py-2 border border-transparent rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
              >
                {cancelling ? 'Cancelling...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminEventRegistrations;
